import {
  ExpandLessRounded,
  ExpandMoreRounded,
  FiberManualRecord,
} from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useNav } from '../../../hooks/useNav';
import { NavItemType } from '../../../types/types';
import Transitions from '../../Transitions';
import NavItem, { LightTooltip } from './NavItem';

type VirtualElement = {
  getBoundingClientRect: () => ClientRect | DOMRect;
  contextElement?: Element;
};

const PopperStyled = styled(Popper)(({ theme }) => ({
  overflow: 'visible',
  zIndex: 1202,
  minWidth: 220,
  '&:before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    top: 38,
    left: -5,
    width: 10,
    height: 10,
    backgroundColor: theme.palette.background.paper,
    transform: 'translateY(-50%) rotate(45deg)',
    zIndex: 120,
    borderLeft: `1px solid ${theme.palette.grey[200]}`,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
}));

interface NavCollapseProps {
  menu: NavItemType;
  level: number;
  selectedItems: string | undefined;
  selectedLevel: number;
  setSelectedItems: Dispatch<SetStateAction<string | undefined>>;
  setSelectedLevel: Dispatch<SetStateAction<number>>;
}

const NavCollapse = ({
  menu,
  level,
  selectedItems,
  selectedLevel,
  setSelectedItems,
  setSelectedLevel,
}: NavCollapseProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { pathname } = useLocation();
  const { drawerOpen, setOpenItem } = useNav();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null | undefined>(null);
  const [anchorEl, setAnchorEl] = useState<
    VirtualElement | (() => VirtualElement) | null | undefined
  >(null);

  const handleClick = (
    event?:
      | React.MouseEvent<HTMLAnchorElement>
      | React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    setAnchorEl(null);
    setSelectedLevel(level);
    if (drawerOpen) {
      setOpen(!open);
      setSelected(!selected ? menu.id : null);
      setSelectedItems(!selected ? menu.id : '');
      if (menu.url) navigate(menu.url);
    } else {
      setAnchorEl(event?.currentTarget);
    }
  };

  const handlerIconLink = () => {
    if (!drawerOpen) {
      if (menu.url) navigate(menu.url);
      setSelected(menu.id);
    }
  };

  const miniMenuOpened = Boolean(anchorEl);

  const handleClose = () => {
    setOpen(false);
    if (!miniMenuOpened) {
      if (!menu.url) {
        setSelected(null);
      }
    }
    setAnchorEl(null);
  };

  useMemo(() => {
    if (selected === selectedItems) {
      if (level === 1) {
        setOpen(true);
      }
    } else {
      if (level === selectedLevel) {
        setOpen(false);
        if (!miniMenuOpened && !drawerOpen && !selected) {
          setSelected(null);
        }
        if (drawerOpen) {
          setSelected(null);
        }
      }
    }
  }, [
    selectedItems,
    level,
    selected,
    miniMenuOpened,
    drawerOpen,
    selectedLevel,
  ]);

  useEffect(() => {
    if (
      menu.id &&
      menu.url &&
      (pathname === menu.url || pathname.includes(menu.url))
    ) {
      setSelected(menu.id);
    }
  }, [pathname, menu.url, menu.id]);

  const checkOpenForParent = (child: NavItemType[], id: string) => {
    child.forEach((item: NavItemType) => {
      if (item.url === pathname) {
        setOpen(true);
        setSelected(id);
      }
    });
  };

  useEffect(() => {
    setOpen(false);
    if (!miniMenuOpened) {
      setSelected(null);
    }
    if (miniMenuOpened) setAnchorEl(null);
    if (menu.children) {
      menu.children.forEach((item: NavItemType) => {
        if (item.children?.length) {
          checkOpenForParent(item.children, menu.id!);
        }
        if (pathname && pathname.includes('product-details')) {
          if (item.url && item.url.includes('product-details')) {
            setSelected(menu.id);
            setOpen(true);
          }
        }
        if (item.url === pathname) {
          setSelected(menu.id);
          setOpen(true);
        }
      });
    }
  }, [pathname, menu.children, miniMenuOpened, menu.id]);

  useEffect(() => {
    if (menu.url === pathname) {
      setOpenItem([menu.id || 'dashboard']);
      setSelected(menu.id);
      setAnchorEl(null);
      setOpen(true);
    }
  }, [pathname, menu, setOpenItem]);

  const menuItems = menu.children?.map((item) => {
    switch (item.type) {
      case 'collapse':
        if (item?.isVisible === false) {
          return null;
        }
        return (
          <NavCollapse
            key={item.id || `collapse-${Math.random()}`}
            menu={item}
            level={level + 1}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedItems={selectedItems}
            selectedLevel={selectedLevel}
          />
        );
      case 'item':
        return (
          <NavItem
            key={item.id || `item-${Math.random()}`}
            item={item}
            level={level + 1}
          />
        );
      default:
        return (
          <Typography
            key={item.id || `error-${Math.random()}`}
            variant="h6"
            color="error"
            align="center"
          >
            Menu Item Error
          </Typography>
        );
    }
  });

  const isVisible =
    typeof menu.isVisible === 'function'
      ? menu.isVisible({})
      : menu.isVisible !== false;

  if (!isVisible) {
    return null;
  }

  const Icon = menu.icon;
  const menuIcon = menu.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1rem' : '1.25rem',
        color:
          selected === menu.id
            ? theme.sidebar.navItem.selected.text
            : theme.sidebar.navItem.default.text,
      }}
    />
  ) : (
    <FiberManualRecord
      sx={{
        width: 6,
        height: 6,
        color: theme.sidebar.navItem.default.text,
      }}
    />
  );

  return (
    <>
      {!drawerOpen ? (
        <LightTooltip title={menu.title} placement="right" arrow>
          <ListItemButton
            selected={selected === menu.id}
            {...(menu.url && { onClick: handlerIconLink })}
            sx={{
              pl: 1.5,
              py: 0.5,
              '&:hover': {
                bgcolor: 'transparent',
              },
              '&.Mui-selected': {
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: 'transparent',
                },
              },
            }}
          >
            <ListItemIcon
              onClick={handleClick}
              sx={{
                minWidth: 26,
                color:
                  selected === menu.id
                    ? theme.sidebar.navItem.selected.text
                    : theme.sidebar.navItem.default.text,
                ...(!drawerOpen && {
                  borderRadius: 1.5,
                  width: 36,
                  height: 36,
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    bgcolor: theme.sidebar.navItem.default.hover.background,
                  },
                }),
                ...(!drawerOpen &&
                  selected === menu.id && {
                    bgcolor: theme.sidebar.navItem.selected.background,
                    '&:hover': {
                      bgcolor: theme.sidebar.navItem.selected.hover.background,
                    },
                  }),
              }}
            >
              {menuIcon}
            </ListItemIcon>
          </ListItemButton>
        </LightTooltip>
      ) : (
        <ListItemButton
          selected={selected === menu.id}
          onClick={handleClick}
          sx={{
            pl: `${level === 1 ? 16 : level * 16}px`,
            py: 0.5,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: theme.sidebar.navItem.default.hover.background,
            },
            '&.Mui-selected': {
              borderRight: `2px solid ${theme.sidebar.navItem.selected.text}`,
              bgcolor: theme.sidebar.navItem.selected.background,
              '&:hover': {
                bgcolor: theme.sidebar.navItem.selected.hover.background,
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 26,
              color:
                selected === menu.id
                  ? theme.sidebar.navItem.selected.text
                  : theme.sidebar.navItem.default.text,
            }}
          >
            {menuIcon}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant="h6"
                color={
                  selected === menu.id
                    ? theme.sidebar.navItem.selected.text
                    : theme.sidebar.navItem.default.text
                }
                sx={{ fontSize: '0.875rem' }}
              >
                {menu.title}
              </Typography>
            }
            secondary={
              menu.caption && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.sidebar.navItem.default.text,
                    opacity: 0.6,
                  }}
                  display="block"
                  gutterBottom
                >
                  {menu.caption}
                </Typography>
              )
            }
          />
          {open ? (
            <ExpandLessRounded
              style={{
                fontSize: '0.75rem',
                marginTop: 1.5,
                color: theme.sidebar.navItem.default.text,
              }}
            />
          ) : (
            <ExpandMoreRounded
              style={{
                fontSize: '0.75rem',
                marginTop: 1.5,
                color: theme.sidebar.navItem.default.text,
              }}
            />
          )}
        </ListItemButton>
      )}

      {miniMenuOpened && (
        <PopperStyled
          open={miniMenuOpened}
          anchorEl={anchorEl}
          placement="right-start"
          style={{
            zIndex: 2001,
          }}
          popperOptions={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [-12, 1],
                },
              },
            ],
          }}
        >
          {({ TransitionProps }) => (
            <Transitions in={miniMenuOpened} {...TransitionProps}>
              <Paper
                sx={{
                  overflow: 'hidden',
                  mt: 1.5,
                  boxShadow: theme.shadows[8],
                  backgroundImage: 'none',
                }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <Box sx={{ p: 1 }}>
                    <List
                      component="nav"
                      sx={{
                        p: 0,
                        '& .MuiListItemIcon-root': {
                          minWidth: 28,
                        },
                      }}
                    >
                      {menuItems}
                    </List>
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Transitions>
          )}
        </PopperStyled>
      )}

      {drawerOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List
            component="nav"
            sx={{
              p: 0,
              '& .MuiListItemIcon-root': {
                minWidth: 28,
              },
            }}
          >
            {menuItems}
          </List>
        </Collapse>
      )}
    </>
  );
};

export default NavCollapse;
